import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { SignInFormType } from './types';

/**
 * Props for the UsernamePasswordFields component
 */
interface UsernamePasswordFieldsProps {
  control: Control<SignInFormType>;
  errors: FieldErrors<SignInFormType>;
  loading: boolean;
  passwordVisibility: boolean;
  onTogglePasswordVisibility: () => void;
  trigger: (name?: 'username' | 'password') => Promise<boolean>;
}

/**
 * Username and Password Input Fields Component
 * 
 * Renders the email and password input fields with proper validation,
 * password visibility toggle, and loading states.
 * 
 * @component
 * @param {UsernamePasswordFieldsProps} props - Component props
 * @returns {React.ReactElement} The username and password fields
 */
export function UsernamePasswordFields({
  control,
  errors,
  loading,
  passwordVisibility,
  onTogglePasswordVisibility,
  trigger,
}: Readonly<UsernamePasswordFieldsProps>): React.ReactElement {
  return (
    <div className="space-y-24">
      <Controller
        name="username"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Email"
            autoFocus
            type="email"
            autoComplete='email'
            error={!!errors.username}
            helperText={errors?.username?.message}
            disabled={field.disabled || loading}
            variant="outlined"
            required
            fullWidth
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            className="relative"
            label="Password"
            type={passwordVisibility ? 'text' : 'password'}
            autoComplete='current-password'
            error={!!errors.password}
            helperText={errors?.password?.message}
            disabled={field.disabled || loading}
            variant="outlined"
            required
            fullWidth
            onPaste={() => {
              // Retrigger validation after paste
              setTimeout(() => trigger('password'), 0);
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        passwordVisibility
                          ? 'hide the password'
                          : 'display the password'
                      }
                      onClick={onTogglePasswordVisibility}
                      onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                        event.preventDefault()
                      }
                      onMouseUp={(event: React.MouseEvent<HTMLButtonElement>) =>
                        event.preventDefault()
                      }
                      edge="end"
                    >
                      {passwordVisibility ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      />
    </div>
  );
}